import imageCompression from 'browser-image-compression'
import { LocalFile } from '@types'

export interface UploadOptions {
  compress?: boolean
  maxSizeMB?: number
  useWebWorker?: boolean
}

interface UseUploadFileHook {
  uploadFile: (url: string, file: LocalFile, options?: UploadOptions) => Promise<void>
}

/**
 * Supported image MIME types for compression
 */
const COMPRESSIBLE_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/bmp']

/**
 * Default max size for compressed images in MB
 */
const DEFAULT_MAX_SIZE_MB = 0.5 // 500 KB

export const useUploadFile = (): UseUploadFileHook => {
  const uploadFile = async (url: string, localFile: LocalFile, options: UploadOptions = {}): Promise<void> => {
    const { data, type } = localFile
    const { compress = true, maxSizeMB = DEFAULT_MAX_SIZE_MB, useWebWorker = true } = options

    try {
      let fileToUpload = data

      if (compress && COMPRESSIBLE_IMAGE_TYPES.includes(type)) {
        fileToUpload = await imageCompression(data, {
          maxSizeMB,
          useWebWorker,
        })
      }

      const response = await fetch(url, {
        method: 'PUT',
        body: fileToUpload,
      })

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status} ${response.statusText}`)
      }

      return
    } catch (error) {
      if (error instanceof Error) {
        console.error(`File upload error: ${error.message}`)
      } else {
        console.error('Unknown file upload error occurred')
      }
      throw error
    }
  }

  return { uploadFile }
}
